/**
 * @file Agenda grid display component.
 * @author Tom Verschoore de la Houssaije
 */
import * as tslib_1 from "tslib";
import { AfterContentChecked, ElementRef, NgZone, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { GridDisplayBaseComponent } from '../grid-display-base/grid-display-base.component';
import { Entity } from 'breeze-client';
import { DomainService } from '../../domain.service';
import { LocalisationService } from '../../core/localisation.service';
import { Router } from '@angular/router';
import { SettingsService } from '../../settings.service';
import { OverlayService } from '../../core/overlay.service';
import { ExecutorsService } from '../executors.service';
import { AlertsService } from '../../core/alerts.service';
import { EventsService } from '../../core/event.service';
import { GridService } from '../grid.service';
import { ResourceService } from '../../resource.service';
import { RoutesService } from '../../routes.service';
import { BackendSettingsService } from '../../backend-settings.service';
/** Agenda grid display component.
 * Part of agenda that shows agenda items.
 */
var AgendaGridDisplayComponent = /** @class */ (function (_super) {
    tslib_1.__extends(AgendaGridDisplayComponent, _super);
    /**
     * Constructs a new AgendaGridDisplayComponent instance.
     * @param domainService The domain service to retrieve the agenda items form the backend.
     * @param localisationService The localization service for multi language support.
     * @param configurationsService The configuration service to get the configuration of the agenda.
     * @param router The router service to listen to navigation events triggering refreshing.
     */
    function AgendaGridDisplayComponent(router, domainService, localisationService, resourceService, settingsService, backendSettingsService, overlayService, executorsService, alertsService, elementRef, ngZone, changeDetector, eventsService, gridService) {
        var _this = _super.call(this, router, domainService, localisationService, settingsService, backendSettingsService, overlayService, executorsService, alertsService, elementRef, ngZone, changeDetector, eventsService, gridService) || this;
        _this.resourceService = resourceService;
        _this.gridService = gridService;
        /** Gets, sets the query options to retrieve the agenda items. */
        _this.queryOptions = {};
        _this.subscriptionPool.add(eventsService.register('expand-grid-changed').subscribe(function () {
            _this.detectChanges();
        }));
        return _this;
    }
    /** Lifecycle hook that is called after every check of a directive's content. */
    AgendaGridDisplayComponent.prototype.ngAfterContentChecked = function () {
        if (this._gridCollections === undefined) {
            this._gridCollections = this.generateGridSegments();
        }
    };
    /**
     * Refresh the current dataset.
     */
    AgendaGridDisplayComponent.prototype.userRefresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _a, resource;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        this.isLoading = true;
                        _a = this;
                        return [4 /*yield*/, this.domainService.getDataSet(this._objects, this._objects.items)];
                    case 1:
                        _a.loadingInProgress = _b.sent();
                        resource = this.getResource();
                        if (resource) {
                            resource.$setRow(this._objects);
                        }
                        this.gridService.level.formatter.refreshWith('AGENDA', this._objects.items);
                        this.loadingInProgress = null;
                        this.eventsService.register('grid-refresh').emit();
                        this.isLoading = false;
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Lifecycle hook that is called after a component's view has been fully initialized.
     */
    AgendaGridDisplayComponent.prototype.ngAfterViewInit = function () {
        var _this = this;
        _super.prototype.ngAfterViewInit.call(this);
        this.subscriptionPool.add(this.gridService.stateChanged.subscribe(function () {
            _this._gridCollections = _this.generateGridSegments();
            _this.refresh();
        }));
    };
    /**
     * Retrieves a new fresh data set form the backend when query parameters are changed.
     * @param {Boolean} [forcing=false] Refresh is forced when true otherwise only when query parameters are changed.
     * @returns {Observable<QueryResult>} An observable of the needed retrieve request otherwise null.
     */
    AgendaGridDisplayComponent.prototype.refresh = function (forcing) {
        if (forcing === void 0) { forcing = false; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var _a, resource;
            return tslib_1.__generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (!(forcing || DomainService.setDynamicParams(this.queryOptions, this.dynamicParameters, this))) return [3 /*break*/, 2];
                        this.lastResourceKey = this.userResourceKey;
                        this.isLoading = true;
                        _a = this;
                        return [4 /*yield*/, this.domainService.getDataSet(this._objects, this._objects.items)];
                    case 1:
                        _a.loadingInProgress = _b.sent();
                        resource = this.getResource();
                        if (resource) {
                            resource.$setRow(this._objects);
                        }
                        this.gridService.level.formatter.refreshWith('AGENDA', this._objects.items);
                        _b.label = 2;
                    case 2:
                        this.loadingInProgress = null;
                        this.isLoading = false;
                        return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(AgendaGridDisplayComponent.prototype, "userResourceKey", {
        /** Gets the key of the selected resource, the owner of the agenda. */
        get: function () {
            if (this.bar === null || this.bar === undefined) {
                return this.resourceService.getResourceOid();
            }
            return this.bar.selectedResource;
        },
        enumerable: true,
        configurable: true
    });
    // Returns the selected resource from the agenda grid bar
    AgendaGridDisplayComponent.prototype.getResource = function () {
        var agendaGridBarComponent = this.bar;
        var entitySelector = agendaGridBarComponent.entitySelector;
        var resource = entitySelector.value;
        return (resource !== null) ? resource : this.resourceService.resource;
    };
    Object.defineProperty(AgendaGridDisplayComponent.prototype, "startDate", {
        /** Gets the start date of the displayed period. */
        get: function () {
            if (this.gridService.level.formatter.queryStartDate == null) {
                return '2017-01-01';
            }
            else {
                return this.gridService.level.formatter.queryStartDate.format('YYYY-MM-DD HH:mm:ss');
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(AgendaGridDisplayComponent.prototype, "endDate", {
        /** Gets the end date of the displayed period. */
        get: function () {
            if (this.gridService.level.formatter.queryFinishDate == null) {
                return '2017-02-01';
            }
            else {
                return this.gridService.level.formatter.queryFinishDate.format('YYYY-MM-DD HH:mm:ss');
            }
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Refreshes if the resource key has changed.
     * @protected
     */
    AgendaGridDisplayComponent.prototype.refreshIfNeeded = function () {
        if (this.lastResourceKey !== this.userResourceKey) {
            this._gridCollections = this.generateGridSegments();
            this.refresh();
        }
    };
    /**
     * Is called when a displayed entity is changed a partial refresh is needed.
     * @param {Entity} entity The changed displayed entity.
     * @param {boolean} selectable Flag indicating whether or not to select the entity when needed.
     */
    AgendaGridDisplayComponent.prototype.refreshFor = function (entity, selectable) {
        if (selectable === void 0) { selectable = true; }
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            var itemOid, planItemNew, wrapper;
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.refresh(selectable)];
                    case 1:
                        _a.sent();
                        if (RoutesService.getDetailId(this.router.routerState) == null) {
                            return [2 /*return*/];
                        }
                        itemOid = entity['Oid'] ? entity['Oid'] : entity['Item_Oid'];
                        planItemNew = this._objects.items.find(function (item) { return item['Item_Oid'] === itemOid; });
                        if (planItemNew) {
                            wrapper = this.findWrapperOf(planItemNew['Item']);
                            this.selectPlanItem(wrapper);
                        }
                        else {
                            // Kon de item niet vinden ik moet details sluiten.
                            this.selectPlanItem(null);
                        }
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Is called after the block name is set and the configuration is loaded.
     */
    AgendaGridDisplayComponent.prototype.afterInitialize = function () {
        var _this = this;
        _super.prototype.afterInitialize.call(this);
        if (this.configuration.data) {
            var entityConfig = this.configuration.data;
            this._objects.sourceName = entityConfig.dataSource;
            if (entityConfig.parameters) {
                this.queryOptions.parameters = Object.assign({}, entityConfig.parameters);
            }
            this.queryOptions.expand = entityConfig.expand;
            this._objects.queryOptions = this.queryOptions;
            DomainService.getDynamicParams(this.queryOptions, this.dynamicParameters);
            this.domainService.registerSource(entityConfig.entity, this._objects);
            this.subscriptionPool.add(this.localisationService.onLanguageChanged.subscribe(function () {
                _this._gridCollections = _this.generateGridSegments();
                _this.refresh();
            }));
        }
    };
    /**
     * Generates the GridSegments a collection of elements to display.
     */
    AgendaGridDisplayComponent.prototype.generateGridSegments = function () {
        // If horizon does not exist; return an empty set
        return this.gridService.level.formatter.agendaGridSegments(this, this.gridService);
    };
    /**
     * Used for check if the current segment needs expansion
     */
    AgendaGridDisplayComponent.prototype.expandGrid = function (segmentsIndex, segmentIndex) {
        if (this.expandedIndex === undefined) {
            return false;
        }
        return (this.gridService.level.name === 'day' ? segmentsIndex : segmentIndex) === this.expandedIndex;
    };
    return AgendaGridDisplayComponent;
}(GridDisplayBaseComponent));
export { AgendaGridDisplayComponent };
